<template>
    <div>
        <back-button :route="{ name: 'finnancial.chargebacks.users' }" />

        <section class="mb-6 bg-white p-6 shadow dark:bg-gray-800 sm:rounded-lg">
            <h3 class="mb-4 text-lg font-medium leading-6 text-gray-900 dark:text-white">
                Importar Transações
            </h3>

            <form @submit.prevent="uploadFile" class="space-y-4">
                <div>
                    <label
                        for="file_input"
                        class="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                        Arquivo Excel (.xlsx)
                    </label>
                    <input
                        id="file_input"
                        ref="file"
                        type="file"
                        accept=".xlsx,.xls"
                        required
                        class="block w-full cursor-pointer rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                    />
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        O arquivo deve conter as colunas: Case Id, Account Number, Transaction Date, Transaction Amount, etc.
                    </p>
                </div>

                <label class="flex items-center space-x-2 text-sm text-gray-900 dark:text-gray-100">
                    <input
                        id="dry-run"
                        v-model="dryRun"
                        type="checkbox"
                        class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>Modo simulação (dry-run) - apenas visualizar sem salvar no banco</span>
                </label>

                <submit-button :loading="uploading">
                    {{ dryRun ? "Simular Processamento" : "Processar Arquivo" }}
                </submit-button>
            </form>
        </section>

        <section v-if="transactions.length > 0" class="mb-8">
            <div class="mb-3 text-sm text-gray-600 dark:text-gray-400">
                <span class="font-semibold">Resumo:</span>
                <span class="text-green-600 dark:text-green-400"> {{ foundCount }} encontrada(s) </span>,
                <span class="text-red-600 dark:text-red-400"> {{ notFoundCount }} não encontrada(s) </span>
                <span v-if="duplicatesCount > 0" class="text-yellow-600 dark:text-yellow-400">
                    , {{ duplicatesCount }} duplicada(s)
                </span>
            </div>

            <div class="not-prose overflow-hidden rounded-xl bg-white shadow dark:bg-gray-800">
                <table class="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                    <thead class="bg-gray-50 text-xs uppercase text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                        <tr>
                            <th class="px-4 py-3 text-left">Case ID</th>
                            <th class="px-4 py-3 text-left">Data</th>
                            <th class="px-4 py-3 text-left">Valor</th>
                            <th class="px-4 py-3 text-left">Cartão</th>
                            <th class="px-4 py-3 text-left">Status</th>
                            <th class="px-4 py-3 text-left">Pré-seleção</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                        <tr
                            v-for="(item, index) in transactions"
                            :key="index"
                            :class="importRowClass(item)"
                        >
                            <td class="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                                {{ item.case_id || "--" }}
                            </td>
                            <td class="px-4 py-3 text-gray-700 dark:text-gray-300">
                                {{ formatDate(item.transaction_date) }}
                            </td>
                            <td class="px-4 py-3 text-gray-700 dark:text-gray-300">
                                {{ formatCurrency(item.transaction_amount) }}
                            </td>
                            <td class="px-4 py-3 text-gray-700 dark:text-gray-300">
                                {{ item.first_6 }}...{{ item.last_4 }}
                            </td>
                            <td class="px-4 py-3">
                                <span :class="importStatusClass(item)">
                                    {{ importStatusLabel(item) }}
                                </span>
                            </td>
                            <td class="px-4 py-3 text-gray-700 dark:text-gray-300">
                                <div v-if="item.transactions && item.transactions.length">
                                    <p class="font-medium text-indigo-600 dark:text-indigo-300">
                                        #{{ item.transactions[0].id }} · {{ formatCurrency(item.transactions[0].paid_amount) }}
                                    </p>
                                    <p v-if="item.transactions.length > 1" class="text-xs text-gray-500 dark:text-gray-400">
                                        +{{ item.transactions.length - 1 }} outra(s) candidata(s)
                                    </p>
                                </div>
                                <span v-else class="text-sm text-gray-400">Sem candidatos</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>

        <section class="mb-8 bg-white p-6 shadow dark:bg-gray-800 sm:rounded-lg">
            <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white">
                        Notificações Visa Verifi
                    </h3>
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                        Revise a pré-seleção, confirme a transação correta ou rejeite notificações inválidas.
                    </p>
                </div>
                <button
                    type="button"
                    @click="loadNotifications()"
                    class="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                    <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                    </svg>
                    Atualizar
                </button>
            </div>

            <table-component
                :loading="loadingNotifications"
                :headers="tableHeaders"
                :pagination="notificationsPagination"
                @fetch="loadNotifications"
            >
                <tr
                    v-for="notification in notifications"
                    :key="notification.id"
                    :class="notificationRowClass(notification)"
                >
                    <td class="table-cell-sm">
                        <div class="font-semibold text-gray-900 dark:text-gray-100">
                            #{{ notification.id }}
                        </div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">
                            {{ formatDate(notification.created_at) }}
                        </div>
                    </td>
                    <td class="table-cell-sm">
                        <span :class="sourceBadgeClass(notification.source)">
                            {{ formatSource(notification.source) }}
                        </span>
                    </td>
                    <td class="table-cell-sm">
                        <div class="font-medium text-gray-900 dark:text-gray-100">
                            {{ notification.case_id }}
                        </div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">
                            {{ formatDate(notification.transaction_date) }}
                        </div>
                    </td>
                    <td class="table-cell-sm text-gray-700 dark:text-gray-300">
                        {{ formatCurrency(notification.transaction_amount) }}
                    </td>
                    <td class="table-cell-sm text-gray-700 dark:text-gray-300">
                        {{ notification.card_first_6 }}...{{ notification.card_last_4 }}
                    </td>
                    <td class="table-cell-sm">
                        <div v-if="notification.transaction" class="space-y-1">
                            <router-link
                                v-if="notification.transaction.order_id"
                                :to="{
                                    name: 'user.order',
                                    params: { order_id: notification.transaction.order_id },
                                }"
                                class="text-indigo-600 hover:text-indigo-800 hover:underline dark:text-indigo-300"
                                target="_blank"
                            >
                                Pedido #{{ notification.transaction.order_id }}
                            </router-link>
                            <p class="text-sm text-gray-700 dark:text-gray-300">
                                {{ formatCurrency(notification.transaction.paid_amount / 100) }}
                            </p>
                        </div>
                        <span v-else class="text-sm text-gray-400">Sem pré-seleção</span>
                    </td>
                    <td class="table-cell-sm">
                        <span :class="confidenceBadgeClass(notification.match_confidence)">
                            {{ confidenceLabel(notification.match_confidence) }}
                        </span>
                    </td>
                    <td class="table-cell-sm">
                        <status
                            v-if="notification.transaction_status"
                            :status="notification.transaction_status"
                            size="sm"
                        />
                        <span v-else class="text-xs text-gray-500 dark:text-gray-400">--</span>
                    </td>
                    <td class="table-cell-sm">
                        <div class="flex flex-wrap gap-2">
                            <button
                                v-if="canProcessNotification(notification)"
                                type="button"
                                :disabled="processingNotification === notification.id"
                                @click="processNotification(notification)"
                                class="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <svg
                                    v-if="processingNotification === notification.id"
                                    class="mr-2 h-3 w-3 animate-spin text-white"
                                    viewBox="0 0 24 24"
                                >
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {{ processLabel(notification) }}
                            </button>
                            <button
                                type="button"
                                @click="openReview(notification)"
                                class="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                Revisar
                            </button>
                        </div>
                    </td>
                </tr>
            </table-component>
        </section>

        <TransitionRoot as="template" :show="reviewModalOpen">
            <Dialog as="div" class="relative z-50" @close="closeReviewModal">
                <TransitionChild
                    as="template"
                    enter="ease-out duration-200"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-150"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <div class="fixed inset-0 bg-black/40" />
                </TransitionChild>

                <div class="fixed inset-0 z-50 overflow-y-auto">
                    <div class="flex min-h-full items-center justify-center p-4">
                        <TransitionChild
                            as="template"
                            enter="ease-out duration-200"
                            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enter-to="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-150"
                            leave-from="opacity-100 translate-y-0 sm:scale-100"
                            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel class="w-full max-w-4xl overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800">
                                <div class="border-b border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900">
                                    <DialogTitle class="text-lg font-medium text-gray-900 dark:text-white">
                                        Revisar Notificação
                                    </DialogTitle>
                                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                        Confirme a transação correta ou rejeite a notificação quando aplicável.
                                    </p>
                                </div>

                                <div class="space-y-6 px-6 py-6">
                                    <div v-if="selectedNotification" class="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm dark:border-gray-700 dark:bg-gray-900">
                                        <div class="grid gap-3 sm:grid-cols-2">
                                            <div>
                                                <span class="font-medium text-gray-700 dark:text-gray-300">Case ID:</span>
                                                <span class="ml-1 text-gray-900 dark:text-gray-100">{{ selectedNotification.case_id }}</span>
                                            </div>
                                            <div>
                                                <span class="font-medium text-gray-700 dark:text-gray-300">Data:</span>
                                                <span class="ml-1 text-gray-900 dark:text-gray-100">{{ formatDate(selectedNotification.transaction_date) }}</span>
                                            </div>
                                            <div>
                                                <span class="font-medium text-gray-700 dark:text-gray-300">Valor:</span>
                                                <span class="ml-1 text-gray-900 dark:text-gray-100">{{ formatCurrency(selectedNotification.transaction_amount) }}</span>
                                            </div>
                                            <div>
                                                <span class="font-medium text-gray-700 dark:text-gray-300">Cartão:</span>
                                                <span class="ml-1 text-gray-900 dark:text-gray-100">{{ selectedNotification.card_first_6 }}...{{ selectedNotification.card_last_4 }}</span>
                                            </div>
                                            <div>
                                                <span class="font-medium text-gray-700 dark:text-gray-300">Confiança:</span>
                                                <span :class="confidenceBadgeClass(selectedNotification.match_confidence)" class="ml-2 inline-flex items-center rounded px-2 py-0.5 text-xs font-medium">
                                                    {{ confidenceLabel(selectedNotification.match_confidence) }}
                                                </span>
                                            </div>
                                            <div v-if="selectedNotification.transaction">
                                                <span class="font-medium text-gray-700 dark:text-gray-300">Pré-seleção atual:</span>
                                                <span class="ml-1 text-gray-900 dark:text-gray-100">#{{ selectedNotification.transaction.id }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <div class="mb-3 flex items-center justify-between">
                                            <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                Candidatas encontradas
                                            </h4>
                                            <div class="flex flex-wrap gap-2 text-xs">
                                                <button
                                                    type="button"
                                                    :disabled="searchingCandidates"
                                                    @click="searchCandidates"
                                                    class="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-3 py-1.5 font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    <svg
                                                        v-if="searchingCandidates"
                                                        class="mr-2 h-3 w-3 animate-spin text-white"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Buscar novamente
                                                </button>
                                                <button
                                                    type="button"
                                                    @click="clearMatches"
                                                    class="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                                                >
                                                    Limpar lista
                                                </button>
                                            </div>
                                        </div>

                                        <div v-if="loadingCandidates" class="flex items-center justify-center py-10">
                                            <svg class="h-6 w-6 animate-spin text-indigo-600" viewBox="0 0 24 24">
                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        </div>

                                        <div v-else-if="candidates.length === 0" class="rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                                            Nenhuma candidata disponível. Busque novamente ou processe a notificação para atualizar os dados.
                                        </div>

                                        <div v-else class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                                            <table class="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                                                <thead class="bg-gray-50 text-xs uppercase text-gray-500 dark:bg-gray-900 dark:text-gray-300">
                                                    <tr>
                                                        <th class="px-4 py-2 text-left">Selecionar</th>
                                                        <th class="px-4 py-2 text-left">Transação</th>
                                                        <th class="px-4 py-2 text-left">Valor</th>
                                                        <th class="px-4 py-2 text-left">Parcelas</th>
                                                        <th class="px-4 py-2 text-left">Data</th>
                                                        <th class="px-4 py-2 text-left">Status</th>
                                                        <th class="px-4 py-2 text-left">Outras notificações</th>
                                                    </tr>
                                                </thead>
                                                <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                                                    <tr
                                                        v-for="candidate in candidates"
                                                        :key="candidate.id"
                                                        :class="candidateRowClass(candidate)"
                                                    >
                                                        <td class="px-4 py-2">
                                                            <input
                                                                type="radio"
                                                                name="candidate"
                                                                :value="candidate.id"
                                                                v-model="selectedCandidateId"
                                                                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                                                            />
                                                        </td>
                                                        <td class="px-4 py-2">
                                                            <div class="font-medium text-gray-900 dark:text-gray-100">
                                                                #{{ candidate.id }}
                                                            </div>
                                                            <router-link
                                                                v-if="candidate.order_id"
                                                                :to="{
                                                                    name: 'user.order',
                                                                    params: { order_id: candidate.order_id },
                                                                }"
                                                                target="_blank"
                                                                class="text-xs text-indigo-600 hover:text-indigo-800 hover:underline dark:text-indigo-300"
                                                            >
                                                                Pedido #{{ candidate.order_id }}
                                                            </router-link>
                                                        </td>
                                                        <td class="px-4 py-2 text-gray-700 dark:text-gray-300">
                                                            {{ formatCurrency(candidate.amount) }}
                                                        </td>
                                                        <td class="px-4 py-2 text-gray-700 dark:text-gray-300">
                                                            <span v-if="candidate.installments > 1">
                                                                {{ candidate.installments }}x de {{ formatCurrency(candidate.installment_value) }}
                                                            </span>
                                                            <span v-else>À vista</span>
                                                        </td>
                                                        <td class="px-4 py-2 text-gray-700 dark:text-gray-300">
                                                            {{ formatDate(candidate.created_at) }}
                                                        </td>
                                                        <td class="px-4 py-2">
                                                            <status :status="candidate.status" size="sm" />
                                                        </td>
                                                        <td class="px-4 py-2 text-xs text-gray-600 dark:text-gray-300">
                                                            <span v-if="candidate.related_notifications && candidate.related_notifications.length">
                                                                {{ candidate.related_notifications.length }} notificação(ões)
                                                            </span>
                                                            <span v-else>--</span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div class="space-y-4">
                                        <div class="grid gap-3 sm:grid-cols-2">
                                            <button
                                                v-if="selectedNotification?.transaction_id"
                                                type="button"
                                                :disabled="reviewActionLoading"
                                                @click="confirmPreSelection"
                                                class="inline-flex items-center justify-center rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                Confirmar pré-seleção
                                            </button>
                                            <button
                                                type="button"
                                                :disabled="!canChangeSelection || reviewActionLoading"
                                                @click="changeSelection"
                                                class="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                Salvar nova associação
                                            </button>
                                        </div>

                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Motivo da rejeição
                                            </label>
                                            <textarea
                                                v-model="rejectReason"
                                                rows="3"
                                                class="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                                                placeholder="Informe o motivo antes de rejeitar a notificação"
                                            ></textarea>
                                            <div class="mt-2 flex justify-end">
                                                <button
                                                    type="button"
                                                    :disabled="reviewActionLoading"
                                                    @click="rejectNotification"
                                                    class="inline-flex items-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    Rejeitar notificação
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900">
                                    <button
                                        type="button"
                                        @click="closeReviewModal"
                                        class="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                                    >
                                        Fechar
                                    </button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script>
import BackButton from "../../../../components/layouts/navigation/BackButton.vue";
import SubmitButton from "../../../../components/forms/SubmitButton.vue";
import TableComponent from "../../../../components/layouts/table/TableComponent.vue";
import Status from "../../../../components/layouts/Status.vue";
import { useToast } from "vue-toastification";
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";

export default {
    components: {
        BackButton,
        SubmitButton,
        TableComponent,
        Status,
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
    },

    data() {
        return {
            uploading: false,
            dryRun: false,
            transactions: [],
            foundCount: 0,
            notFoundCount: 0,
            duplicatesCount: 0,
            notifications: [],
            notificationsPagination: null,
            loadingNotifications: false,
            processingNotification: null,
            reviewModalOpen: false,
            selectedNotification: null,
            candidates: [],
            preSelectedTransactionId: null,
            selectedCandidateId: null,
            loadingCandidates: false,
            searchingCandidates: false,
            reviewActionLoading: false,
            rejectReason: "",
            tableHeaders: [
                "ID",
                "Fonte",
                "Case ID",
                "Valor",
                "Cartão",
                "Pré-seleção",
                "Confiança",
                "Status Transação",
                "Ações",
            ],
        };
    },

    setup() {
        const toast = useToast();
        return { toast };
    },

    mounted() {
        this.loadNotifications();
    },

    computed: {
        canChangeSelection() {
            if (!this.selectedCandidateId) {
                return false;
            }
            if (!this.selectedNotification) {
                return false;
            }
            return this.selectedCandidateId !== this.preSelectedTransactionId;
        },
    },

    methods: {
        formatCurrency(value) {
            if (value === null || value === undefined || value === "") {
                return "R$ 0,00";
            }

            const amount = Number(value);
            if (Number.isNaN(amount)) {
                return "R$ 0,00";
            }

            return amount.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
            });
        },

        formatDate(date) {
            if (!date) {
                return "--";
            }
            return new Date(date).toLocaleDateString("pt-BR");
        },

        importStatusLabel(item) {
            if (!item.found) {
                return "Não encontrada";
            }
            if (item.is_duplicate) {
                return "Duplicada";
            }
            return "Pré-associada";
        },

        importStatusClass(item) {
            if (!item.found) {
                return "inline-flex rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-200";
            }
            if (item.is_duplicate) {
                return "inline-flex rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200";
            }
            return "inline-flex rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-200";
        },

        importRowClass(item) {
            if (!item.found) {
                return "bg-red-50/60 dark:bg-red-900/10";
            }
            if (item.is_duplicate) {
                return "bg-yellow-50/60 dark:bg-yellow-900/10";
            }
            return "bg-green-50/60 dark:bg-green-900/10";
        },

        notificationRowClass(notification) {
            switch (notification.status) {
                case "processed":
                    return "bg-green-50/60 dark:bg-green-900/10";
                case "pending_review":
                    return "bg-blue-50/60 dark:bg-blue-900/10";
                case "not_found":
                    return "bg-red-50/60 dark:bg-red-900/10";
                case "error":
                    return "bg-yellow-50/60 dark:bg-yellow-900/10";
                default:
                    return "";
            }
        },

        sourceBadgeClass(source) {
            const base = "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium";
            if (source === "visa_webhook") {
                return `${base} bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200`;
            }
            return `${base} bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200`;
        },

        formatSource(source) {
            if (source === "visa_webhook") {
                return "Visa Webhook";
            }
            if (source === "excel_import") {
                return "Importação Excel";
            }
            return source || "--";
        },

        confidenceLabel(confidence) {
            switch (confidence) {
                case "high":
                    return "Alta";
                case "medium":
                    return "Média";
                case "low":
                    return "Baixa";
                case "manual_review":
                    return "Revisão Manual";
                default:
                    return "Indefinida";
            }
        },

        confidenceBadgeClass(confidence) {
            const base = "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium";
            switch (confidence) {
                case "high":
                    return `${base} bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200`;
                case "medium":
                    return `${base} bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200`;
                case "low":
                    return `${base} bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200`;
                case "manual_review":
                    return `${base} bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-300`;
                default:
                    return `${base} bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-300`;
            }
        },

        candidateRowClass(candidate) {
            if (candidate.id === this.preSelectedTransactionId) {
                return "bg-blue-50 dark:bg-blue-900/20";
            }
            return "";
        },

        processLabel(notification) {
            if (notification.status === "pending") {
                return "Processar";
            }
            return "Reprocessar";
        },

        canProcessNotification(notification) {
            const statuses = ["pending", "error", "not_found"];
            return (
                statuses.includes(notification.status) ||
                !notification.transaction_id
            );
        },

        async uploadFile() {
            if (!this.$refs.file?.files?.length) {
                this.toast.error("Por favor, selecione um arquivo.");
                return;
            }

            this.uploading = true;

            try {
                const formData = new FormData();
                formData.append("file", this.$refs.file.files[0]);
                formData.append("dry_run", this.dryRun ? "1" : "0");

                const { data } = await axios.post(
                    "/api/global-chargebacks/upload",
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } },
                );

                this.transactions = data.data || [];
                this.foundCount = data.found || 0;
                this.notFoundCount = data.not_found || 0;
                this.duplicatesCount = data.duplicates || 0;

                const message = `${data.found} encontrada(s), ${data.not_found} não encontrada(s)${
                    data.duplicates > 0 ? `, ${data.duplicates} duplicada(s)` : ""
                }`;

                this.toast.success(
                    this.dryRun ? `[SIMULAÇÃO] ${message}` : message,
                );

                if (!this.dryRun) {
                    this.loadNotifications();
                }
            } catch (error) {
                console.error("Erro ao processar arquivo:", error);
                this.toast.error("Erro ao processar arquivo de importação.");
            } finally {
                this.uploading = false;
            }
        },

        async loadNotifications(page = null) {
            if (page && this.notificationsPagination) {
                this.notificationsPagination.current_page = page;
            }

            this.loadingNotifications = true;

            try {
                const params = {
                    page:
                        this.notificationsPagination?.current_page ??
                        page ??
                        1,
                };

                const { data } = await axios.get(
                    "/api/global-chargebacks/notifications",
                    { params },
                );

                this.notifications = data.data || [];
                this.notificationsPagination = {
                    total: data.total,
                    per_page: data.per_page,
                    current_page: data.current_page,
                    last_page: data.last_page,
                    from: data.from,
                    to: data.to,
                };
            } catch (error) {
                console.error("Erro ao carregar notificações:", error);
                this.toast.error("Erro ao carregar notificações.");
            } finally {
                this.loadingNotifications = false;
            }
        },

        updateNotificationInList(updatedNotification) {
            const index = this.notifications.findIndex(
                (item) => item.id === updatedNotification.id,
            );

            if (index === -1) {
                return;
            }

            updatedNotification.order_id =
                updatedNotification.transaction?.order_id ?? null;
            updatedNotification.transaction_status =
                updatedNotification.transaction?.status ?? null;
            updatedNotification.requires_review =
                updatedNotification.status === "pending_review";
            updatedNotification.candidate_count =
                updatedNotification.potential_matches?.length ?? 0;
            updatedNotification.has_pre_selection =
                updatedNotification.transaction_id !== null &&
                !updatedNotification.association_confirmed;
            updatedNotification.is_confirmed =
                updatedNotification.association_confirmed;

            this.notifications.splice(index, 1, updatedNotification);
        },

        async processNotification(notification) {
            this.processingNotification = notification.id;

            try {
                const { data } = await axios.post(
                    `/api/global-chargebacks/notifications/${notification.id}/process`,
                );

                this.toast.success("Notificação processada com sucesso.");
                this.updateNotificationInList(data.data);
            } catch (error) {
                console.error("Erro ao processar notificação:", error);
                this.toast.error("Erro ao processar notificação.");
            } finally {
                this.processingNotification = null;
            }
        },

        async openReview(notification) {
            this.selectedNotification = { ...notification };
            this.reviewModalOpen = true;
            await this.loadCandidatesForNotification(notification.id);
        },

        closeReviewModal() {
            this.reviewModalOpen = false;
            this.selectedNotification = null;
            this.candidates = [];
            this.preSelectedTransactionId = null;
            this.selectedCandidateId = null;
            this.rejectReason = "";
        },

        async loadCandidatesForNotification(notificationId) {
            this.loadingCandidates = true;

            try {
                const { data } = await axios.get(
                    `/api/global-chargebacks/notifications/${notificationId}/candidates`,
                );

                this.candidates = data.candidates || [];
                this.preSelectedTransactionId =
                    data.pre_selected_transaction_id || null;
                this.selectedCandidateId =
                    data.pre_selected_transaction_id || null;
                this.selectedNotification = {
                    ...this.selectedNotification,
                    ...data.notification,
                    transaction: data.pre_selected_transaction || null,
                };
            } catch (error) {
                console.error("Erro ao carregar candidatos:", error);
                this.toast.error("Erro ao carregar candidatos.");
            } finally {
                this.loadingCandidates = false;
            }
        },

        async confirmPreSelection() {
            if (!this.selectedNotification?.transaction_id) {
                this.toast.error("Não há pré-seleção para confirmar.");
                return;
            }

            this.reviewActionLoading = true;

            try {
                const { data } = await axios.post(
                    `/api/global-chargebacks/notifications/${this.selectedNotification.id}/review`,
                    { action: "confirm" },
                );

                this.toast.success("Pré-seleção confirmada com sucesso.");
                this.updateNotificationInList(data.notification);
                this.closeReviewModal();
            } catch (error) {
                console.error("Erro ao confirmar pré-seleção:", error);
                this.toast.error("Erro ao confirmar pré-seleção.");
            } finally {
                this.reviewActionLoading = false;
            }
        },

        async changeSelection() {
            if (!this.selectedCandidateId) {
                this.toast.error("Selecione uma transação candidata.");
                return;
            }

            this.reviewActionLoading = true;

            try {
                const { data } = await axios.post(
                    `/api/global-chargebacks/notifications/${this.selectedNotification.id}/review`,
                    {
                        action: "change",
                        transaction_id: this.selectedCandidateId,
                    },
                );

                this.toast.success("Associação atualizada com sucesso.");
                this.updateNotificationInList(data.notification);
                this.closeReviewModal();
            } catch (error) {
                console.error("Erro ao alterar associação:", error);
                this.toast.error("Erro ao alterar associação.");
            } finally {
                this.reviewActionLoading = false;
            }
        },

        async rejectNotification() {
            if (this.rejectReason.trim().length === 0) {
                this.toast.error("Informe um motivo para rejeitar a notificação.");
                return;
            }

            this.reviewActionLoading = true;

            try {
                const { data } = await axios.post(
                    `/api/global-chargebacks/notifications/${this.selectedNotification.id}/review`,
                    {
                        action: "reject",
                        reject_reason: this.rejectReason.trim(),
                    },
                );

                this.toast.warning("Notificação rejeitada.");
                this.updateNotificationInList(data.notification);
                this.closeReviewModal();
            } catch (error) {
                console.error("Erro ao rejeitar notificação:", error);
                this.toast.error("Erro ao rejeitar notificação.");
            } finally {
                this.reviewActionLoading = false;
            }
        },

        async searchCandidates() {
            this.searchingCandidates = true;

            try {
                const { data } = await axios.post(
                    `/api/global-chargebacks/notifications/${this.selectedNotification.id}/search-candidates`,
                );

                this.toast.success(
                    `${data.candidates_found} candidata(s) encontrada(s) com confiança ${this.confidenceLabel(
                        data.confidence,
                    )}.`,
                );

                await this.loadCandidatesForNotification(
                    this.selectedNotification.id,
                );
                this.updateNotificationInList(data.notification);
            } catch (error) {
                console.error("Erro ao buscar candidatas:", error);
                this.toast.error("Erro ao buscar candidatas.");
            } finally {
                this.searchingCandidates = false;
            }
        },

        async clearMatches() {
            if (
                !confirm(
                    "Tem certeza que deseja limpar a lista de candidatas? Esta ação não pode ser desfeita.",
                )
            ) {
                return;
            }

            try {
                await axios.post(
                    `/api/global-chargebacks/notifications/${this.selectedNotification.id}/clear-matches`,
                );

                this.toast.success("Lista de candidatas limpa.");
                this.candidates = [];
                this.preSelectedTransactionId = null;
                this.selectedCandidateId = null;
                this.selectedNotification = {
                    ...this.selectedNotification,
                    transaction: null,
                    transaction_id: null,
                    match_confidence: "manual_review",
                };
                this.updateNotificationInList({ ...this.selectedNotification });
            } catch (error) {
                console.error("Erro ao limpar candidatas:", error);
                this.toast.error("Erro ao limpar candidatas.");
            }
        },
    },
};
</script>
